/* Import components */

import React, { useEffect, useState } from 'react';
import { DataTablePaginated } from '../components';
import { axios } from '../libs/axios';
import ReactPaginate from 'react-paginate';
import { useSelector } from 'react-redux';
import { User } from '../states/actions';
import { useHistory } from 'react-router-dom';
import logger from '../utils/logger';


/* Format date */

function formatDateDMY(date) {
	if (!date) return '';
	const d = new Date(date);
	const day = d.getDate().toString().padStart(2, '0');
	const month = (d.getMonth() + 1).toString().padStart(2, '0');
	const year = d.getFullYear();
	return `${day}.${month}.${year}`;
}

/* Convert line breaks to HTML */

function convertLineBreaksToHtml(text) {
	if (!text) return '';
	return text.replace(/\n/g, '<br>');
}

/* Export inquiries */

export const Inquiries = () => {
	const user = useSelector(User.get());
	const history = useHistory();
	const [projects,setProjects]         = useState([]);
	const [rawProjects, setRawProjects]  = useState([]);
	const [selectedProjects,setSelected] = useState([]);
	const [loading,setLoading]           = useState(false);
	const [currentPage,setCurrentPage]   = useState(1);
	const [totalRecords,setTotalRecords] = useState(0);
	const [sizePerPage,setSizePerPage]   = useState(100);
	const [tooltip,setTooltip]           = useState({visible:false,text:'',x:0,y:0});
	const [search, setSearch]            = useState('');
	const [sortField, setSortField]      = useState('createdAt');
	const [sortOrder, setSortOrder]      = useState('desc');

	const [showProjectModal, setShowProjectModal] = useState(false);
	const [editingProjectId, setEditingProjectId] = useState(null);
	
	/* New project modal state */
	const [showNewProjectModal, setShowNewProjectModal] = useState(false);
	const [newProjectForm, setNewProjectForm] = useState({
		project_name: '',
		project_description: '',
		project_type: 'inquiry',
		related_client_id: '',
		client_name: '',
		business_name: '',
		client_type: 'person',
		status: 'waiting'
	});
	const [clients, setClients] = useState([]);
	const [isCreating, setIsCreating] = useState(false);

	/* Client search functionality */
	const [clientSearch, setClientSearch] = useState('');
	const [filteredClients, setFilteredClients] = useState([]);
	const [showClientDropdown, setShowClientDropdown] = useState(false);
	
	/* Check for inquiry claim error from redirect */
	useEffect(() => {
		const errorMessage = sessionStorage.getItem('inquiryClaimError');
		if (errorMessage) {
			// Show the error notification
			window.nError({
				title: 'Already Claimed',
				text: errorMessage,
				icon: 'fa-exclamation-triangle'
			});
			// Clear the error from sessionStorage
			sessionStorage.removeItem('inquiryClaimError');
		}
	}, []); // Run once on mount
	
	/* Fetch inquiries */
	
	const fetchProjects = async(page=1, size=100, q='', sortField='createdAt', sortOrder='desc')=> {
		setLoading(true);
		try {
			const {projects: rows, total} = await axios.get('/projects', {
				params: {
					page,
					sizePerPage: size,
					q,
					show_inquiries_only: true, // Only fetch inquiries
					sortField,
					sortOrder,
				}
			});
			setRawProjects(rows);
			setProjects(rows);
			setTotalRecords(total);
			setSelected([]);
		} finally { setLoading(false); }
	};

	/* Handle table sorting */
	
	const handleTableChange = (type, { sortField, sortOrder }) => {
		if (type === 'sort') {
			setSortField(sortField);
			setSortOrder(sortOrder);
			setCurrentPage(1); // Reset to first page when sorting
		}
	};
	
	/* Fetch available clients for dropdown */
	const fetchAvailableClients = async() => {
		try {
			if (user?.role === 'lawyer') {
				const { clients: myClients } = await axios.get('/clients/my-clients');
				const normalized = (myClients || []).map((c) => ({
					_id: c._id,
					display_name: c.display_name,
					firstname: c.display_name,
					lastname: '',
					type: 'person',
				}));
				setClients(normalized);
				setFilteredClients(normalized);
				return;
			}
			const { clients } = await axios.get('/clients', {
				params: { sizePerPage: 10000 },
			});
			setClients(clients);
			setFilteredClients(clients);
		} catch (error) {
			logger.error('Error fetching clients:', error);
		}
	};

	/* Client search functionality */
	const handleClientSearch = (searchTerm) => {
		setClientSearch(searchTerm);
		
		if (searchTerm.trim() === '') {
			setFilteredClients(clients);
			setShowClientDropdown(false);
		} else {
			const searchLower = searchTerm.toLowerCase();
			
			// Filter and score clients based on relevance
			const filtered = clients.filter(client => {
				if (user?.role === 'lawyer') {
					const name = (client.display_name || `${client.firstname} ${client.lastname}`).toLowerCase();
					return name.includes(searchLower);
				}
				const fullName = `${client.firstname} ${client.lastname}`.toLowerCase();
				const email = (client.email || '').toLowerCase();
				const businessName = client.business_name ? client.business_name.toLowerCase() : '';
				
				return fullName.includes(searchLower) || 
					   email.includes(searchLower) || 
					   businessName.includes(searchLower);
			}).sort((a, b) => {
				// Prioritize business name matches for business clients
				if (a.type === 'business' && a.business_name && a.business_name.toLowerCase().includes(searchLower)) {
					if (b.type !== 'business' || !b.business_name || !b.business_name.toLowerCase().includes(searchLower)) {
						return -1; // a comes first
					}
				}
				if (b.type === 'business' && b.business_name && b.business_name.toLowerCase().includes(searchLower)) {
					if (a.type !== 'business' || !a.business_name || !a.business_name.toLowerCase().includes(searchLower)) {
						return 1; // b comes first
					}
				}
				
				// Then prioritize exact name matches
				const aFullName = `${a.firstname} ${a.lastname}`.toLowerCase();
				const bFullName = `${b.firstname} ${b.lastname}`.toLowerCase();
				
				if (aFullName.startsWith(searchLower) && !bFullName.startsWith(searchLower)) return -1;
				if (bFullName.startsWith(searchLower) && !aFullName.startsWith(searchLower)) return 1;
				
				// Finally sort alphabetically
				return aFullName.localeCompare(bFullName);
			});
			
			setFilteredClients(filtered);
			setShowClientDropdown(true);
		}
	};

	const selectClient = (client) => {
		const isBusiness = client.type === 'business';
		const representativeName = client.display_name || `${client.firstname} ${client.lastname}`.trim();
		setNewProjectForm({
			...newProjectForm,
			related_client_id: client._id,
			client_name: representativeName,
			business_name: client.business_name || '',
			client_type: client.type || 'person'
		});
		
		const displayName = user?.role === 'lawyer'
			? representativeName
			: (isBusiness
				? `${client.business_name} (Business)`
				: `${client.firstname} ${client.lastname} (${client.email})`);
		setClientSearch(displayName);
		setShowClientDropdown(false);
	};

	const clearClientSelection = () => {
		setNewProjectForm({
			...newProjectForm,
			related_client_id: '',
			client_name: '',
			business_name: '',
			client_type: 'person'
		});
		setClientSearch('');
		setShowClientDropdown(false);
	};
	
	useEffect(()=> { 
		fetchProjects(currentPage, sizePerPage, search, sortField, sortOrder); 
		fetchAvailableClients();
	}, [currentPage, sizePerPage, search, sortField, sortOrder]);

	// Close client dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (!event.target.closest('.client-search-container')) {
				setShowClientDropdown(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);
	
	/* Search fallback */
	
	useEffect(() => {
		const s = search.trim().toLowerCase();
		setProjects(s ? rawProjects.filter(p => {
			const createdDate  = formatDateDMY(p.createdAt).toLowerCase();
			const deadlineTask = p.related_tasks?.find(t => t.deadline);
			const deadlineDate = deadlineTask ? formatDateDMY(deadlineTask.deadline).toLowerCase() : '';
			const totalMinutes = p.related_tasks?.reduce((sum, task) => sum + (+task.task_duration || 0), 0) || 0;
			const totalHours   = (totalMinutes / 60).toFixed(2);

			return deadlineDate.includes(s)
				|| p.project_name.toLowerCase().includes(s)
				|| p.project_no.toLowerCase().includes(s)
				|| p.client_name.toLowerCase().includes(s)
				|| (p.business_name && p.business_name.toLowerCase().includes(s))
				|| (p.project_owner && p.project_owner.toLowerCase().includes(s))
				|| totalHours.toString().includes(s)
				|| createdDate.includes(s);
		}) : rawProjects);
	}, [search, rawProjects]);
	
	/* Delete project */
	
	const confirmDelete = (text, onYes)=> {
		let n; n = window.nConfirm({
			title:'Delete inquiry?',
			text,
			icon:'fa-exclamation-circle',
			onYes: async()=> { await onYes(); n?.close(); },
			onNo : ()=> { n?.close(); }
		});
	};
	
	const deleteProject = (id,project_no)=> {
		confirmDelete(`<span>${project_no}</span>`, async()=> {
			await axios.delete('/projects/bulk',{ projectIds:[id] });
			fetchProjects(currentPage, sizePerPage, search);
		});
	};
	
	/* New project handlers */
	const openNewProject = () => {
		setNewProjectForm({
			project_name: '',
			project_description: '',
			project_type: 'inquiry', // Always inquiry type
			related_client_id: '',
			client_name: '',
			business_name: '',
			client_type: 'person',
			status: 'waiting'
		});
		setClientSearch('');
		setShowClientDropdown(false);
		setIsCreating(true);
		setShowNewProjectModal(true);
	};
	
	const handleCreateProject = async (e) => {
		e.preventDefault();
		try {
			if (isCreating) {
				// Create new inquiry - no owner required
				const projectData = {
					...newProjectForm,
					project_type: 'inquiry' // Ensure it's always inquiry
				};
				
				await axios.post('/projects/create', projectData);
				window.nSuccess({title: 'Success!', text: 'Inquiry created successfully!', icon: 'fa-check-square'});
			} else {
				// Update existing inquiry
				await axios.patch(`/projects/${editingProjectId}`, newProjectForm);
				window.nSuccess({title: 'Success!', text: 'Inquiry updated successfully!', icon: 'fa-check-square'});
			}
			setShowNewProjectModal(false);
			setClientSearch('');
			setShowClientDropdown(false);
			fetchProjects(currentPage, sizePerPage, search);
		} catch (error) {
			logger.error('Error creating/updating inquiry:', error);
			window.nError({title: 'Error!', text: isCreating ? 'Error creating inquiry. Please try again.' : 'Error updating inquiry. Please try again.', icon: 'fa-exclamation-triangle'});
		}
	};
	
	/* Select rows */
	
	const handleSelectRow = id => setSelected(sp=>sp.includes(id)?sp.filter(x=>x!==id):[...sp,id]);
	const handleSelectAll = ()=> {setSelected(sp=>sp.length===projects.length?[]:projects.map(p=>p._id));};
	
	/* Render status */
	
	const renderStatusIcon = s => {
		let c = 'status status-';
		if (s === 'active') c += 'active';
		else if (s === 'waiting') c += 'waiting';
		else if (s === 'finished') c += 'finished';
		else if (s === 'cancelled') c += 'cancelled';
		return (
			<div className={c}>
				<span className="indicator" />
			</div>
		);
	};
	
	const getRowClass = r => {
		// Highlight recent inquiries (added in last 24 hours) with light blue background - only for admin
		if (user && user.role === 'admin' && r.createdAt) {
			const createdDate = new Date(r.createdAt);
			const now = new Date();
			const hoursDiff = (now - createdDate) / (1000 * 60 * 60);
			
			if (hoursDiff <= 24) {
				return 'row-recent-inquiry';
			}
		}
		
		if (r.status === 'waiting') return 'row-waiting';
		if (r.status === 'active') return 'row-active';
		if (r.status === 'finished') return 'row-finished';
		if (r.status === 'cancelled') return 'row-cancelled';
		return 'row-default';
	};
	
	/* Table columns: order Created -> Inquiry -> Description -> Legal field; no status, no type */
	
	const tableColumns = [
		{
			dataField: 'createdAt',
			text: 'Created',
			classes: 'cell-created',
			headerClasses: 'cell-created',
			style: { width: '105px', minWidth: '105px' },
			headerStyle: { width: '105px', minWidth: '105px' },
			formatter: d => formatDateDMY(d),
		},
		{
			dataField: 'project_name',
			text: 'Inquiry',
			classes: 'cell-project',
			headerClasses: 'cell-project',
			style: { width: '210px', minWidth: '210px', maxWidth: '210px' },
			headerStyle: { width: '210px', minWidth: '210px', maxWidth: '210px' },
			formatter: (c,r)=>
				<div
					className='inner'
					onClick={e =>{e.stopPropagation(); history.push(`/projects/${r.project_no}`);}}
					style={{ width: '200px !important', paddingRight: '10px', minWidth: '200px', maxWidth: '200px' }}
					>
					{r.project_name}
				</div>
		},
		{
			dataField: 'project_description',
			text: 'Description',
			classes: 'cell-description',
			headerClasses: 'cell-description',
			style: { width: '400px', minWidth: '400px', maxWidth: '400px', paddingRight: '20px', textAlign: 'left' },
			headerStyle: { width: '400px', minWidth: '500px', maxWidth: '500px', textAlign: 'left' },
			formatter: (c,r)=> {
				const maxLength = 200;
				const description = r.project_description || '';
				const truncated = description.length > maxLength 
					? description.substring(0, maxLength) + '...' 
					: description;
				
				return (
					<div 
						className='inner' 
						style={{ whiteSpace: 'pre-wrap', width: '400px', minWidth: '400px', paddingRight: '20px', maxWidth: '400px', textAlign: 'left' }}
						title={description.length > maxLength ? description : ''}
					>
						{truncated}
					</div>
				);
			}
		},
		{
			dataField: 'project_tag',
			text: 'Legal field',
			classes: 'cell-tag',
			headerClasses: 'cell-tag',
			style: { textAlign: 'left' },
			headerStyle: { textAlign: 'left' },
			formatter: (c, r) => (
				r.project_tag ? (
					<div className="tags">
						<ul>
							<li className="sector"><span>{r.project_tag}</span></li>
						</ul>
					</div>
				) : null
			)
		},
	];
	
	/* Render HTML */
	
	return (
		<div id="inquiries" className="row table_v2 projects">
			<div className="col p-0">
				<div className="x_panel chatbot-builder">
					<div className="header-menu d-flex justify-content-between align-items-center">
						<div className="header-title d-flex align-items-center">
							<h1>Legal Inquiries</h1>
						</div>
					<div className="header-actions d-flex align-items-center">
						<div className="action action-search">
							<input
								type="text"
								placeholder="Search by inquiry name, number, client, or business…"
								value={search}
								onChange={e=>{ setSearch(e.target.value); setCurrentPage(1); }}
							/>
							<i class="fa-solid fa-magnifying-glass"></i>
						</div>
						{/* Only admins can create new inquiries */}
						{user && user.role === 'admin' && (
							<div className="action action-addnew">
								<button className="button button-blue" onClick={openNewProject}>
									+ Add New
								</button>
							</div>
						)}
						</div>
					</div>
					<div className="x_content">
						<div className="page-container d-flex flex-column">
							{loading ? <div>Loading…</div> : (
							<>
								<div className= "page-header d-flex justify-content-end align-items-center">
									<div className='filter filter-pagesize d-flex justify-content-end align-items-center'>
									<select id="pageSize" value={sizePerPage} onChange={e=>{setSizePerPage(+e.target.value);setCurrentPage(1);}}>
										{[100,200,300,500].map(n=><option key={n} value={n}>{n}</option>)}
									</select>
									</div>
								</div>
								<div className="page-content nopaginate">
									<div className="inquiries-table-wrap">
										<DataTablePaginated
										data          = {projects}
										columns       = {tableColumns}
										onTableChange = {handleTableChange}
										onRowClick    = {(row) => history.push(`/projects/${row.project_no}`)}
										rowClasses    = {getRowClass}
										/>
									</div>
								</div>
								<div className="page-footer d-flex justify-content-between align-items-center">
									<div className='filter filter-pagination d-flex align-items-center'>
										{Math.ceil(totalRecords / sizePerPage) > 0 && (
										<ReactPaginate
										containerClassName    = {'pagination'}
										activeClassName       = {'active'}
										previousLinkClassName = {'fa-regular fa-chevron-left'}
										nextLinkClassName     = {'fa-regular fa-chevron-right'}
										previousClassName     = {currentPage === 1 ? 'd-none' : 'previous'}
										nextClassName         = {currentPage === Math.ceil(totalRecords / sizePerPage) ? 'd-none' : 'next'}
										previousLabel         = {'Previous'}
										nextLabel             = {'Next'}
										breakLabel            = {'…'}
										pageCount             = {Math.ceil(totalRecords/sizePerPage)}
										marginPagesDisplayed  = {2}
										pageRangeDisplayed    = {5}
										onPageChange          = {e=>setCurrentPage(e.selected+1)}
										forcePage             = {currentPage-1}
										/>
										)}
									</div>
									<div className='filter filter-pagesize d-flex justify-content-end align-items-center'>
									<select id="pageSize" value={sizePerPage} onChange={e=>{setSizePerPage(+e.target.value);setCurrentPage(1);}}>
										{[100,200,300,500].map(n=><option key={n} value={n}>{n}</option>)}
									</select>
									</div>
								</div>
							</>
							)}
						</div>
					</div>
				</div>
			</div>
			
			{/* New Inquiry Modal */}
			{showNewProjectModal && (
				<div className="modal project">
					<div className="modal-content" onClick={e => e.stopPropagation()}>
						<div className="modal-header">
							<span className="modalclose" onClick={() => setShowNewProjectModal(false)}>&times;</span>
							<h2>{isCreating ? 'New Inquiry' : 'Edit Inquiry'}</h2>
						</div>
						<div className="modal-body" style={{maxHeight: '70vh', overflowY: 'auto'}}>
							<div className="modal-form">
							<form onSubmit={handleCreateProject}>
								<div className="form-row col-full">
									<label>Inquiry Name:</label>
									<input
										type="text"
										placeholder="Inquiry Name"
										value={newProjectForm.project_name}
										onChange={e => setNewProjectForm({...newProjectForm, project_name: e.target.value})}
										required
									/>
								</div>
								<div className="form-row col-full">
									<label>Inquiry Description:</label>
									<textarea
										placeholder="Inquiry Description"
										value={newProjectForm.project_description}
										onChange={e => setNewProjectForm({...newProjectForm, project_description: e.target.value})}
										required
									/>
								</div>
								
								<div className="form-row col-full">
									<label>Client:</label>
									<div className="client-search-container" style={{position: 'relative'}}>
										<input
											type="text"
											placeholder="Search client by name, email, or business name..."
											value={clientSearch}
											onChange={e => handleClientSearch(e.target.value)}
											onFocus={() => {
												if (clientSearch.trim() === '' && clients.length > 0) {
													setFilteredClients(clients);
													setShowClientDropdown(true);
												} else if (filteredClients.length > 0) {
													setShowClientDropdown(true);
												}
											}}
											style={{
												width: '100%',
												paddingRight: newProjectForm.related_client_id ? '30px' : '10px'
											}}
											required={!newProjectForm.related_client_id}
										/>
									{newProjectForm.related_client_id && (
										<button
											type="button"
											onClick={clearClientSelection}
											style={{
												position: 'absolute',
												right: '8px',
												top: '50%',
												transform: 'translateY(-50%)',
												background: 'none',
												border: 'none',
												cursor: 'pointer',
												color: '#999',
												fontSize: '16px'
											}}
											title="Clear selection"
										>
											×
										</button>
									)}
									{showClientDropdown && filteredClients.length > 0 && (
										<div className="client-dropdown" style={{
											position: 'absolute',
											top: '100%',
											left: 0,
											right: 0,
											backgroundColor: 'white',
											border: '1px solid #ccc',
											borderTop: 'none',
											maxHeight: '250px',
											overflowY: 'auto',
											zIndex: 1000,
											boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
										}}>
											{filteredClients.slice(0, 20).map(client => (
												<div
													key={client._id}
													className="client-option"
													onClick={() => selectClient(client)}
													style={{
														padding: '10px 12px',
														cursor: 'pointer',
														borderBottom: '1px solid #eee',
														backgroundColor: newProjectForm.related_client_id === client._id ? '#f0f8ff' : 'white',
														transition: 'background-color 0.2s ease'
													}}
													onMouseEnter={e => {
														if (newProjectForm.related_client_id !== client._id) {
															e.currentTarget.style.backgroundColor = '#f5f5f5';
														}
													}}
													onMouseLeave={e => {
														if (newProjectForm.related_client_id !== client._id) {
															e.currentTarget.style.backgroundColor = 'white';
														}
													}}
												>
													{client.type === 'business' ? (
														<>
															<div style={{fontWeight: 'bold', marginBottom: '2px', pointerEvents: 'none', color: '#032097'}}>
																{client.business_name}
																<span style={{fontSize: '10px', background: '#032097', color: 'white', padding: '1px 4px', borderRadius: '2px', marginLeft: '6px'}}>
																	BUSINESS
																</span>
															</div>
															<div style={{fontSize: '11px', color: '#666', pointerEvents: 'none'}}>
																Representative: {client.firstname} {client.lastname}
															</div>
															<div style={{fontSize: '11px', color: '#666', pointerEvents: 'none'}}>
																{client.email}
															</div>
														</>
													) : (
														<>
															<div style={{fontWeight: 'bold', marginBottom: '2px', pointerEvents: 'none'}}>
																{client.firstname} {client.lastname}
															</div>
															<div style={{fontSize: '12px', color: '#666', pointerEvents: 'none'}}>
																{client.email}
															</div>
														</>
													)}
												</div>
											))}
											{filteredClients.length > 20 && (
												<div style={{padding: '8px 12px', fontSize: '12px', color: '#666', textAlign: 'center', backgroundColor: '#f9f9f9'}}>
													Showing first 20 results. Continue typing to narrow down search.
												</div>
											)}
											{filteredClients.length === 0 && clientSearch.trim() !== '' && (
												<div style={{padding: '12px', fontSize: '12px', color: '#666', textAlign: 'center'}}>
													No clients found matching "{clientSearch}"
												</div>
											)}
										</div>
									)}
									</div>
								</div>
								
								<div className="form-row col-full row-actions">
									<button type="button" className="button button-large" onClick={() => setShowNewProjectModal(false)}>Cancel</button>
									<button type="submit" className="button button-large button-blue">{isCreating ? 'Create' : 'Update'}</button>
								</div>
							</form>
							</div>
						</div>
					</div>
				</div>
			)}
			
			{/* Tooltip */}
			{
				tooltip.visible && (
					<div
					className='d-flex flex-column info-tooltip'
					style={{top: tooltip.y + 10, left: tooltip.x + 10}}
					dangerouslySetInnerHTML={{__html: tooltip.text}}
					/>
				)
			}
		</div>
	);
};
